import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import {
  InstagramAccount,
  Interaction,
  InteractionStatus,
  InteractionType,
  MetricsSummary,
} from "../api/types";
import { humanizeError, humanizePayload } from "../lib/errorMessages";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import Avatar from "../components/ui/Avatar";
import {
  IconActivity,
  IconAlert,
  IconChat,
  IconCheck,
  IconClose,
  IconComment,
  IconEye,
  IconFilter,
  IconHeart,
  IconUserMinus,
  IconUserPlus,
} from "../components/ui/Icons";

const TYPE_KEYS: InteractionType[] = [
  "view_story",
  "like_story",
  "like_post",
  "send_dm",
  "follow",
  "unfollow",
  "comment",
];

const STATUS_PILL: Record<InteractionStatus, string> = {
  success: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
  failed: "bg-rose-500/15 text-rose-300 ring-rose-400/30",
  skipped: "bg-slate-500/20 text-slate-300 ring-slate-500/30",
};

const TYPE_ICONS: Record<InteractionType, JSX.Element> = {
  view_story: <IconEye size={14} />,
  like_story: <IconHeart size={14} />,
  like_post: <IconHeart size={14} />,
  send_dm: <IconChat size={14} />,
  follow: <IconUserPlus size={14} />,
  unfollow: <IconUserMinus size={14} />,
  comment: <IconComment size={14} />,
};

const TYPE_TONES: Record<InteractionType, string> = {
  view_story: "text-sky-300",
  like_story: "text-rose-300",
  like_post: "text-pink-300",
  send_dm: "text-emerald-300",
  follow: "text-violet-300",
  unfollow: "text-slate-300",
  comment: "text-amber-300",
};

export default function ActivityLog() {
  const { t } = useTranslation();
  const accountsQuery = useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const accountFromUrl = searchParams.get("account");
  const [igAccountId, setIgAccountId] = useState<string | null>(accountFromUrl);
  const [type, setType] = useState<InteractionType | "">("");
  const [status, setStatus] = useState<InteractionStatus | "">("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!accountsQuery.data?.length) return;
    const known = new Set(accountsQuery.data.map((a) => a.id));
    if (igAccountId && known.has(igAccountId)) return;
    const next =
      accountFromUrl && known.has(accountFromUrl)
        ? accountFromUrl
        : accountsQuery.data[0].id;
    setIgAccountId(next);
    if (accountFromUrl && !known.has(accountFromUrl)) {
      const sp = new URLSearchParams(searchParams);
      sp.delete("account");
      setSearchParams(sp, { replace: true });
    }
  }, [accountsQuery.data, igAccountId, accountFromUrl, searchParams, setSearchParams]);

  useEffect(() => {
    if (!igAccountId) return;
    if (searchParams.get("account") === igAccountId) return;
    const sp = new URLSearchParams(searchParams);
    sp.set("account", igAccountId);
    setSearchParams(sp, { replace: true });
  }, [igAccountId, searchParams, setSearchParams]);

  const interactionsQuery = useQuery({
    queryKey: ["interactions", igAccountId, type, status, page],
    queryFn: async () => {
      const { data } = await api.get<{
        items: Interaction[];
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>("/api/interactions", {
        params: {
          igAccountId,
          type: type || undefined,
          status: status || undefined,
          page,
          pageSize: 25,
        },
      });
      return data;
    },
    enabled: !!igAccountId,
  });

  // Fetch the 24h summary just for the KPI strip - it's cheap and matches the dashboard.
  const summaryQuery = useQuery({
    queryKey: ["metrics-summary", igAccountId],
    queryFn: async () => {
      const { data } = await api.get<MetricsSummary>("/api/metrics/summary", {
        params: { igAccountId },
      });
      return data;
    },
    enabled: !!igAccountId,
  });

  const items = interactionsQuery.data?.items || [];
  const pagination = interactionsQuery.data?.pagination;
  const accounts = accountsQuery.data || [];
  const selectedAccount = accounts.find((a) => a.id === igAccountId);

  // Aggregate the per-type buckets into total success / failed / skipped for last 24h.
  const last24h = useMemo(() => {
    const s = summaryQuery.data?.last24h;
    if (!s) return { success: 0, failed: 0, skipped: 0 };
    return (Object.values(s) as Array<{ success: number; failed: number; skipped: number }>)
      .reduce(
        (acc, row) => ({
          success: acc.success + row.success,
          failed: acc.failed + row.failed,
          skipped: acc.skipped + row.skipped,
        }),
        { success: 0, failed: 0, skipped: 0 }
      );
  }, [summaryQuery.data]);

  const total24h = last24h.success + last24h.failed + last24h.skipped;
  const successRate = total24h === 0 ? 0 : Math.round((last24h.success / total24h) * 100);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.activity")}
        title={t("activity.title")}
        subtitle={t("activity.subtitle")}
        icon={<IconActivity />}
        actions={
          accounts.length > 0 && (
            <AccountFilter
              accounts={accounts}
              selected={igAccountId}
              onSelect={(id) => {
                setIgAccountId(id);
                setPage(1);
              }}
            />
          )
        }
      />

      {/* KPI strip */}
      {selectedAccount && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi
            tone="brand"
            label={t("activity.summary.totalLast24h")}
            value={total24h.toLocaleString()}
          />
          <Kpi
            tone="emerald"
            label={t("activity.summary.successRate")}
            value={`${successRate}%`}
          />
          <Kpi
            tone="rose"
            label={t("activity.summary.failed")}
            value={last24h.failed.toLocaleString()}
          />
          <Kpi
            tone="slate"
            label={t("activity.summary.skipped")}
            value={last24h.skipped.toLocaleString()}
          />
        </div>
      )}

      {/* Filter chips */}
      <div className="card">
        <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
          <IconFilter size={14} />
          {t("activity.filters.type")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip
            active={type === ""}
            onClick={() => {
              setType("");
              setPage(1);
            }}
          >
            {t("activity.filters.allTypes")}
          </FilterChip>
          {TYPE_KEYS.map((k) => (
            <FilterChip
              key={k}
              active={type === k}
              onClick={() => {
                setType(k);
                setPage(1);
              }}
              icon={TYPE_ICONS[k]}
            >
              {t(`interaction.type.${k}`)}
            </FilterChip>
          ))}
        </div>

        <div className="mt-4 mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
          <IconFilter size={14} />
          {t("activity.filters.status")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip
            active={status === ""}
            onClick={() => {
              setStatus("");
              setPage(1);
            }}
          >
            {t("activity.filters.allStatuses")}
          </FilterChip>
          <FilterChip
            active={status === "success"}
            onClick={() => {
              setStatus("success");
              setPage(1);
            }}
            tone="emerald"
            icon={<IconCheck size={12} />}
          >
            {t("interaction.status.success")}
          </FilterChip>
          <FilterChip
            active={status === "failed"}
            onClick={() => {
              setStatus("failed");
              setPage(1);
            }}
            tone="rose"
            icon={<IconAlert size={12} />}
          >
            {t("interaction.status.failed")}
          </FilterChip>
          <FilterChip
            active={status === "skipped"}
            onClick={() => {
              setStatus("skipped");
              setPage(1);
            }}
            tone="slate"
            icon={<IconClose size={12} />}
          >
            {t("interaction.status.skipped")}
          </FilterChip>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<IconActivity size={26} />}
          title={t("activity.empty")}
        />
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="space-y-2 sm:hidden">
            {items.map((row) => {
              const friendlyError = humanizeError(row.error, t);
              const friendlyPayload = humanizePayload(row.payload, t);
              return (
                <div key={row._id} className="card !p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={clsx(
                        "inline-flex items-center gap-1.5 text-sm font-medium",
                        TYPE_TONES[row.type]
                      )}
                    >
                      {TYPE_ICONS[row.type]}
                      {t(`interaction.type.${row.type}`)}
                    </span>
                    <span className={clsx("pill ring-1", STATUS_PILL[row.status])}>
                      {t(`interaction.status.${row.status}`)}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    {format(new Date(row.createdAt), "MMM d, HH:mm:ss")} · @
                    {row.targetUsername || t("common.dash")}
                  </div>
                  {row.error ? (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-rose-300">
                        {friendlyError ?? row.error}
                      </summary>
                      {friendlyError && (
                        <pre className="mt-1 whitespace-pre-wrap break-words text-[10px] text-slate-500">
                          {row.error}
                        </pre>
                      )}
                    </details>
                  ) : friendlyPayload ? (
                    <div className="mt-2 text-xs">
                      <span className="line-clamp-2 text-slate-300">{friendlyPayload}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* sm+: table */}
          <div className="card hidden overflow-x-auto !p-0 sm:block">
            <table className="min-w-full divide-y divide-slate-800/80 text-sm">
              <thead className="bg-slate-900/60 text-[11px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">{t("activity.table.when")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("activity.table.type")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("activity.table.target")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("activity.table.status")}</th>
                  <th className="px-4 py-3 text-left font-medium">{t("activity.table.details")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80">
                {items.map((row) => {
                  const friendlyError = humanizeError(row.error, t);
                  const friendlyPayload = humanizePayload(row.payload, t);
                  return (
                    <tr key={row._id} className="transition hover:bg-slate-900/50">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                        {format(new Date(row.createdAt), "MMM d, HH:mm:ss")}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={clsx(
                            "inline-flex items-center gap-1.5",
                            TYPE_TONES[row.type]
                          )}
                        >
                          {TYPE_ICONS[row.type]}
                          {t(`interaction.type.${row.type}`)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-200">
                        @{row.targetUsername || t("common.dash")}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={clsx("pill ring-1", STATUS_PILL[row.status])}>
                          {t(`interaction.status.${row.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {row.error ? (
                          <span
                            className="text-rose-300"
                            title={friendlyError ? row.error : undefined}
                          >
                            {friendlyError ?? row.error}
                          </span>
                        ) : friendlyPayload ? (
                          <span className="line-clamp-1 max-w-md text-slate-300">
                            {friendlyPayload}
                          </span>
                        ) : (
                          t("common.dash")
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-slate-400">
            {t("activity.pagination.summary", {
              page: pagination.page,
              totalPages: pagination.totalPages,
              total: pagination.total,
            })}
          </span>
          <div className="flex gap-2">
            <button
              className="btn-secondary btn-sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("activity.pagination.prev")}
            </button>
            <button
              className="btn-secondary btn-sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("activity.pagination.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Account filter (segmented control when ≤ 3 accounts, select otherwise)
 * ------------------------------------------------------------------------ */

function AccountFilter({
  accounts,
  selected,
  onSelect,
}: {
  accounts: InstagramAccount[];
  selected: string | null;
  onSelect: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  if (accounts.length <= 3) {
    return (
      <div className="flex w-full items-center gap-1 rounded-xl border border-slate-800/80 bg-slate-900/50 p-1 sm:w-auto">
        {accounts.map((a) => {
          const active = a.id === selected;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a.id)}
              className={clsx(
                "flex flex-1 items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm transition sm:flex-initial",
                active
                  ? "bg-slate-800 text-white shadow-inner shadow-black/20"
                  : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
              )}
              aria-pressed={active}
            >
              <Avatar name={a.igUsername} size="xs" />
              <span className="truncate font-medium">@{a.igUsername}</span>
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <label className="flex w-full items-center gap-2 sm:w-auto">
      <span className="sr-only">{t("activity.filters.account")}</span>
      <select
        className="input sm:w-56"
        value={selected || ""}
        onChange={(e) => onSelect(e.target.value)}
      >
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            @{a.igUsername}
          </option>
        ))}
      </select>
    </label>
  );
}

/* --------------------------------------------------------------------------
 *  KPI strip cell
 * ------------------------------------------------------------------------ */

function Kpi({
  tone,
  label,
  value,
}: {
  tone: "brand" | "emerald" | "rose" | "slate";
  label: string;
  value: string;
}): JSX.Element {
  const styles =
    tone === "brand"
      ? "border-brand-400/30 bg-gradient-to-br from-brand-500/15 to-fuchsia-500/5"
      : tone === "emerald"
      ? "border-emerald-500/30 bg-emerald-500/5"
      : tone === "rose"
      ? "border-rose-500/30 bg-rose-500/5"
      : "border-slate-800/80 bg-slate-900/40";
  return (
    <div className={clsx("rounded-2xl border p-4", styles)}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-white">{value}</div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Filter chip
 * ------------------------------------------------------------------------ */

function FilterChip({
  active,
  onClick,
  icon,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon?: JSX.Element;
  tone?: "emerald" | "rose" | "slate";
  children: React.ReactNode;
}): JSX.Element {
  const activeStyles =
    tone === "emerald"
      ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200"
      : tone === "rose"
      ? "border-rose-400/50 bg-rose-500/15 text-rose-200"
      : tone === "slate"
      ? "border-slate-400/40 bg-slate-700/40 text-slate-100"
      : "border-brand-400/50 bg-brand-500/15 text-brand-100";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition",
        active
          ? activeStyles
          : "border-slate-700/70 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:bg-slate-800 hover:text-slate-200"
      )}
    >
      {icon}
      {children}
    </button>
  );
}
